import { useCallback, useRef, useMemo, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ContentEditable from "react-contenteditable";
import { Input, message, Spin, Popover, Tooltip } from "antd";
import { ApolloError } from "@apollo/client";

import DeviceSelector from "components/device-selector/device-selector";
import { ReactComponent as ThumbsDownIcon } from "../../assets/images/thumbs-down.svg";
import { ReactComponent as ThumbsUpIcon } from "../../assets/images/thumbs-up.svg";
import CommentPopup, { Comment } from "components/comment-popup/comment-popup";
import SignUpModal from "components/sign-up-modal/sign-up-modal";

import routeNames from "constants/route-names";
import { AiChatOrigin, GuideStatus, UserRoleValue } from "constants/field-values";
import { GetGuideQuery } from "graphql/queries/get-guide.generated";
import { useAddGuideFeedbackMutation } from "graphql/mutations/add-guide-feedback.generated";
import { useUpdateGuideFeedbackByIdMutation } from "graphql/mutations/update-guide-feedback-by-id.generated";
import { useAddGuideCommentMutation } from "graphql/mutations/add-guide-comment.generated";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { useAiChatStatus, aiChatStatusInitialValue } from "state/reactive-vars/ai-chat-status-var";
import { getDistinctProperties, getGuideProductPhotos, guideReadTimeCalculator } from "utils/helpers";
import { SelectedProduct } from "routes/guide-details/guide-details";

import LoadingEllipsis from "components/loading-ellipsis/loading-ellipsis";
import useScreenSize from "hooks/useScreenSize";
import { useBrand } from "context/BrandContext";

const { TextArea } = Input;

// just to simplify type references
type Guide = NonNullable<GetGuideQuery["guides_by_pk"]>;

interface GuideDetailsHeadCardProps {
	isUserEditing?: boolean;
	guide: Guide;
	comments: Partial<Comment>[];
	onSave: () => Promise<void>;
	onDelete: () => void;
	updateLoading?: boolean;
	deleteLoading?: boolean;
	stateManagement: {
		title: string;
		setTitle: (title: string) => void;
		description: string;
		setDescription: (description: string) => void;
		selectedDevices: SelectedProduct[];
		setSelectedDevices: (devices: SelectedProduct[]) => void;
	};
}

const GuideDetailsHeadCard = ({
	isUserEditing,
	guide,
	comments,
	onSave,
	onDelete,
	updateLoading = false,
	deleteLoading = false,
	stateManagement: { title, setTitle, description, setDescription, selectedDevices, setSelectedDevices }
}: GuideDetailsHeadCardProps) => {
	const navigate = useNavigate();
	const { loggedUserValue, isUserAnon } = useLoggedUser();
	const { theme } = useBrand();
	const { updateAiChatStatus } = useAiChatStatus();
	const titleRef = useRef<HTMLDivElement>(null);
	const descriptionRef = useRef<HTMLDivElement>(null);

	const [savedIsHelpful, setSavedIsHelpful] = useState<boolean | null>(null);
	const [feedbackId, setFeedbackId] = useState<string | null>(null);
	const [isHelpfulInput, setIsHelpfulInput] = useState<boolean | null>(null);
	const [feedbackPopoverOpen, setFeedbackPopoverOpen] = useState(false);
	const [feedbackNotes, setFeedbackNotes] = useState<string>("");
	const [isHovered, setIsHovered] = useState(false);
	const [showCommentPopup, setShowCommentPopup] = useState(false);
	const [showSignUpModal, setShowSignUpModal] = useState(false);
	const commentIconRef = useRef<HTMLButtonElement>(null);
	const mobileCommentIconRef = useRef<HTMLButtonElement>(null);
	const screenSize = useScreenSize();

	// Reference for the original notes
	const originalNotesRef = useRef<string>("");

	const isGuideDraft = guide.status === GuideStatus.draft;

	useEffect(() => {
		const firstFeedback = guide?.guide_feedbacks?.[0];
		if (firstFeedback) {
			setSavedIsHelpful(firstFeedback.helpful);
			setFeedbackId(firstFeedback.id);
			if (firstFeedback.notes) {
				setFeedbackNotes(firstFeedback.notes);
				originalNotesRef.current = firstFeedback.notes;
			}
		}
	}, [guide?.guide_feedbacks]);

	const canUserEdit = useMemo(() => {
		if (!guide) return false;
		return (
			loggedUserValue.role === UserRoleValue.admin_user ||
			guide.created_by_user_id === loggedUserValue.id ||
			(loggedUserValue.role === UserRoleValue.vendor_manager &&
				loggedUserValue.organization_id === guide.organization_id)
		);
	}, [guide, loggedUserValue]);

	const photoLinks = getGuideProductPhotos(guide);
	const estimatedReadTime = guideReadTimeCalculator(guide.guide_steps);
	const updatedDate = new Date(guide.updated_at).toLocaleDateString("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric"
	});

	const handleOnCompleteAddGuideFeedbacksMutation = useCallback((data: any) => {
		const { id, helpful, notes } = data.insert_guide_feedbacks_one;
		setFeedbackId(id);
		setSavedIsHelpful(helpful);
		setIsHelpfulInput(helpful);
		if (notes) {
			setFeedbackNotes(notes);
			originalNotesRef.current = notes;
		}
	}, []);

	const handleOnErrorAddGuideFeedbacksMutation = useCallback((error: ApolloError) => {
		console.log("useAddGuideFeedbackMutation Error: ", error);
		message.error("Failed to save feedback. Please try again.");
	}, []);

	const [addGuideFeedbackMutation, { loading: loadingAddGuideFeedback }] = useAddGuideFeedbackMutation({
		errorPolicy: "none",
		fetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorAddGuideFeedbacksMutation,
		onCompleted: handleOnCompleteAddGuideFeedbacksMutation
	});

	const handleOnCompleteUpdateGuideFeedbackByIdMutation = useCallback((data: any) => {
		const { helpful, notes } = data.update_guide_feedbacks_by_pk;
		setSavedIsHelpful(helpful);
		if (notes !== undefined) {
			setFeedbackNotes(notes || "");
			originalNotesRef.current = notes || "";
		}
	}, []);

	const handleOnErrorUpdateGuideFeedbackByIdMutation = useCallback((error: ApolloError) => {
		console.log("useUpdateGuideFeedbackByIdMutation Error: ", error);
		message.error("Failed to update feedback. Please try again.");
	}, []);

	const [updateGuideFeedbackByIdMutation, { loading: loadingUpdateGuideFeedbackById }] =
		useUpdateGuideFeedbackByIdMutation({
			errorPolicy: "none",
			fetchPolicy: "network-only",
			notifyOnNetworkStatusChange: true,
			onError: handleOnErrorUpdateGuideFeedbackByIdMutation,
			onCompleted: handleOnCompleteUpdateGuideFeedbackByIdMutation
		});

	const handleThumbClick = useCallback(
		async (helpful: boolean) => {
			// anon
			if (isUserAnon) {
				setShowSignUpModal(true);
				return null;
			}

			setIsHelpfulInput(helpful);
			setFeedbackPopoverOpen(true);

			if (savedIsHelpful === helpful && feedbackId) {
				return;
			}

			try {
				if (feedbackId) {
					const result = await updateGuideFeedbackByIdMutation({
						variables: {
							id: feedbackId,
							_set: {
								helpful: helpful
							}
						}
					});

					if (result.data) {
						setSavedIsHelpful(helpful);
						setIsHelpfulInput(helpful);
					}
				} else {
					await addGuideFeedbackMutation({
						variables: {
							input: {
								guide_id: guide?.id,
								created_by_user_id: loggedUserValue.id,
								helpful: helpful
							}
						}
					});
				}
			} catch (error) {
				console.error("Error updating feedback helpful:", error);
			}
		},
		[
			feedbackId,
			savedIsHelpful,
			guide?.id,
			loggedUserValue.id,
			addGuideFeedbackMutation,
			updateGuideFeedbackByIdMutation
		]
	);

	const handleUpdateFeedback = useCallback(async () => {
		if (feedbackNotes === originalNotesRef.current) {
			setFeedbackPopoverOpen(false);
			return;
		}

		if (!feedbackId) {
			console.error("No feedback ID available");
			setFeedbackPopoverOpen(false);
			return;
		}

		try {
			await updateGuideFeedbackByIdMutation({
				variables: {
					id: feedbackId,
					_set: {
						notes: feedbackNotes
					}
				}
			});

			setFeedbackPopoverOpen(false);
		} catch (error) {
			console.error("Error updating feedback notes:", error);
		}
	}, [feedbackId, feedbackNotes, updateGuideFeedbackByIdMutation]);

	const navigateToEdit = () => {
		navigate("edit");
	};

	const processedDeviceData = useMemo(() => {
		const selected_products_ids: string[] = [];
		const installed_products_ids: string[] = [];
		const properties_ids: string[] = [];

		selectedDevices?.forEach(element => {
			selected_products_ids.push(element.id);

			element.installed_products?.forEach(ip => {
				installed_products_ids.push(ip.id);

				if (ip.property?.id) {
					properties_ids.push(ip.property.id);
				}
			});
		});

		return { selected_products_ids, installed_products_ids, properties_ids };
	}, [selectedDevices]);

	const handleOnClickAI = useCallback(() => {
		// anon
		if (!loggedUserValue.id) return null;
		const topic = `I understand that you may have a question related to ${guide.title}.`;

		updateAiChatStatus({
			...aiChatStatusInitialValue,
			chatOrigin: AiChatOrigin.SPECIALTY,
			chatTopic: topic,
			guide_id: guide.id,
			selected_products_ids: processedDeviceData.selected_products_ids,
			selected_installed_products_ids: processedDeviceData.installed_products_ids,
			selected_properties_ids: processedDeviceData.properties_ids
		});

		navigate(routeNames.AI_CHAT);
	}, [guide.title, processedDeviceData, updateAiChatStatus, navigate]);

	const copyLinkToClipboard = useCallback(async () => {
		try {
			if (!navigator.clipboard) {
				message.error("Clipboard not supported in this browser");
				return;
			}

			await navigator.clipboard.writeText(window.location.href);
			message.success("Link copied to clipboard!");
		} catch (error) {
			console.error("Failed to copy link: ", error);
			message.error("Failed to copy link to clipboard");
		}
	}, []);

	const [addGuideComment, { loading: addingComment }] = useAddGuideCommentMutation();

	// Filter comments for the head card (step_number = 0)
	const headCardComments = useMemo(() => {
		if (isUserEditing) return [];

		return comments.filter(comment => comment.step_number === 0);
	}, [comments, isUserEditing]);

	const handleAddComment = async (text: string) => {
		if (isUserEditing) return;

		try {
			await addGuideComment({
				variables: {
					comment: {
						guide_id: guide.id,
						step_number: 0, // 0 for head card comments
						comment: text,
						created_by_user_id: loggedUserValue?.id
					}
				},
				refetchQueries: ["getGuideComments"],
				awaitRefetchQueries: true
			});
			message.success("Comment added successfully!");
		} catch (error) {
			console.error("Failed to add comment:", error);
			message.error("Failed to add comment. Please try again.");
		}
	};

	const relatedProperties = getDistinctProperties(guide);

	const lastSelectedFeedbackIcon = useMemo(() => {
		if (isHelpfulInput !== null) {
			return isHelpfulInput;
		} else if (typeof savedIsHelpful === "boolean") {
			return savedIsHelpful;
		}
	}, [isHelpfulInput, savedIsHelpful]);

	return (
		<div
			className="guide-details-head-card"
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			{isGuideDraft && isUserEditing && canUserEdit && (
				<div className="guide-details-head-card-draft-edit-banner">
					<span>Review and edit your Guide.</span>
				</div>
			)}
			<div className="guide-details-head-card-header">
				{photoLinks.length > 0 && (
					<div className="guide-details-head-card-device-images">
						{photoLinks.map((src, idx) => (
							<img key={idx} src={src} />
						))}
					</div>
				)}
				<ContentEditable
					innerRef={titleRef}
					html={title}
					disabled={!isUserEditing}
					onChange={e => setTitle(e.target.value)}
					tagName="h2"
					className="guide-details-head-card-title"
				/>
				<div className="guide-details-head-card-actions">
					{isUserEditing ? (
						<>
							{!isGuideDraft && (
								<>
									<button
										className="guide-details-head-card-delete-button"
										title="Delete"
										onClick={onDelete}
										disabled={deleteLoading || updateLoading}
									>
										<img src={`${process.env.PUBLIC_URL}/assets/images/icons/trashcan.svg`} />
									</button>
									<button className="highnote-button" title="Save" onClick={onSave} disabled={updateLoading}>
										{updateLoading ? (
											<LoadingEllipsis text="Saving" />
										) : (
											<>
												<img src={`${process.env.PUBLIC_URL}/assets/images/icons/checkmark.svg`} />
												Done editing
											</>
										)}
									</button>
								</>
							)}
						</>
					) : (
						<>
							{canUserEdit && (
								<button title="Edit" onClick={navigateToEdit}>
									<img src={`${process.env.PUBLIC_URL}/assets/images/icons/edit-pencil-2.svg`} />
								</button>
							)}
							<button title="Link" onClick={copyLinkToClipboard}>
								<img src={`${process.env.PUBLIC_URL}/assets/images/icons/link.svg`} />
							</button>

							<div className="guide-details-head-card-feedback-container">
								{loadingAddGuideFeedback || loadingUpdateGuideFeedbackById ? (
									<Spin size="small" />
								) : (
									<>
										<button title="Like" onClick={() => handleThumbClick(true)}>
											<ThumbsUpIcon
												className={
													lastSelectedFeedbackIcon
														? "guide-details-head-card-feedback-selected-icon"
														: "guide-details-head-card-feedback-icon"
												}
												width="20"
												height="20"
											/>
										</button>
										<button title="Dislike" onClick={() => handleThumbClick(false)}>
											<ThumbsDownIcon
												className={
													lastSelectedFeedbackIcon === false
														? "guide-details-head-card-feedback-selected-icon"
														: "guide-details-head-card-feedback-icon"
												}
												width="20"
												height="20"
											/>
										</button>
									</>
								)}
							</div>

							<button
								ref={mobileCommentIconRef}
								title="Comments"
								className="guide-details-head-card-mobile-comment-button"
							>
								{headCardComments.length > 0 && (
									<span className="guide-details-head-card-comment-count">{headCardComments.length}</span>
								)}
								<img
									className="guide-details-head-card-comment-button-img"
									src={
										headCardComments.length > 0
											? `${process.env.PUBLIC_URL}/assets/images/icons/message-text-square.svg`
											: `${process.env.PUBLIC_URL}${theme.commentIcon}`
									}
									onClick={() => {
										if (isUserAnon) {
											setShowSignUpModal(true);
										} else {
											setShowCommentPopup(!showCommentPopup);
										}
									}}
								/>
							</button>

							<Popover
								trigger="click"
								placement="bottomRight"
								open={feedbackPopoverOpen}
								onOpenChange={open => {
									setFeedbackPopoverOpen(open);
									if (!open) {
										setFeedbackNotes(originalNotesRef.current);
										setIsHelpfulInput(savedIsHelpful);
									}
								}}
								rootClassName="guide-details-head-card-feedback-popover"
								content={
									<div className="guide-details-head-card-feedback-popover-content">
										<div className="guide-details-head-card-feedback-popover-row">
											<span className="guide-details-head-card-feedback-popover-title">
												Help Highnote AI improve. Leave feedback below.
											</span>
											<img
												src={`${process.env.PUBLIC_URL}/assets/images/icons/x-black.svg`}
												onClick={() => {
													setFeedbackPopoverOpen(false);
													setIsHelpfulInput(savedIsHelpful);
													setFeedbackNotes(originalNotesRef.current);
												}}
											/>
										</div>
										<TextArea
											placeholder="What was helpful about this Guide? How can we make it better?"
											className="guide-details-head-card-feedback-popover-input"
											value={feedbackNotes}
											autoComplete="off"
											onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
												setFeedbackNotes(event.target.value);
											}}
											allowClear
											autoSize
										/>
										<button
											title="Submit Feedback"
											className="guide-details-head-card-feedback-submit-button"
											onClick={handleUpdateFeedback}
										>
											Submit feedback
										</button>
									</div>
								}
							/>
						</>
					)}
				</div>
			</div>
			<div className="guide-details-head-card-meta">
				<span>
					<img src={`${process.env.PUBLIC_URL}/assets/images/icons/compass.svg`} alt="steps" />{" "}
					{`${guide.guide_steps.length} ${guide.guide_steps.length > 1 ? "steps" : "step"}`}
				</span>
				<span>
					<img src={`${process.env.PUBLIC_URL}/assets/images/icons/stopwatch.svg`} alt="duration" />
					{`${estimatedReadTime} ${estimatedReadTime > 1 ? "minutes" : "minute"}`}
				</span>
				<span>
					<img src={`${process.env.PUBLIC_URL}/assets/images/icons/calendar.svg`} alt="calendar" />
					{updatedDate}
				</span>
				<span>
					<img src={`${process.env.PUBLIC_URL}/assets/images/icons/user-edit.svg`} alt="author" />
					{guide.author?.first_name} {guide.author?.last_name}
				</span>
			</div>
			<ContentEditable
				innerRef={descriptionRef}
				html={description}
				disabled={!isUserEditing}
				onChange={e => setDescription(e.target.value)}
				tagName="p"
				className="guide-details-head-card-desc"
			/>
			<div className="guide-details-head-card-footer">
				<div className="guide-details-head-card-details">
					{isUserEditing ? (
						<DeviceSelector
							selectedDevices={selectedDevices}
							onDeviceSelect={setSelectedDevices}
							isUserEditing={isUserEditing}
						/>
					) : (
						<div>
							<span>Devices:</span> {guide.product_guides.map(pg => pg?.product?.name).join(", ")}
						</div>
					)}
					{relatedProperties.size > 0 && (
						<div>
							<span>Properties:</span> {Array.from(relatedProperties).join(", ")}
						</div>
					)}
				</div>
				{!isUserEditing && (
					<button className="highnote-button" onClick={handleOnClickAI}>
						Ask a question about this
					</button>
				)}
			</div>

			{(isHovered || showCommentPopup || !!headCardComments.length) && (
				<button ref={commentIconRef} title="Comments" className="guide-details-head-card-comment-button">
					{headCardComments.length > 0 && (
						<span className="guide-details-head-card-comment-count">{headCardComments.length}</span>
					)}
					<Tooltip
						overlayClassName="guide-details-head-card-tooltip"
						color="black"
						placement="bottom"
						title="Leave a comment on this Guide"
						mouseLeaveDelay={0}
					>
						<img
							className="guide-details-head-card-comment-button-img"
							src={
								headCardComments.length > 0
									? `${process.env.PUBLIC_URL}/assets/images/icons/message-text-square.svg`
									: `${process.env.PUBLIC_URL}${theme.commentIcon}`
							}
							onClick={() => {
								if (isUserAnon) {
									setShowSignUpModal(true);
								} else {
									setShowCommentPopup(!showCommentPopup);
								}
							}}
						/>
					</Tooltip>
				</button>
			)}

			{showCommentPopup && !isUserAnon && (
				<CommentPopup
					comments={headCardComments as Required<Comment>[]}
					onAddComment={handleAddComment}
					onClose={() => setShowCommentPopup(false)}
					anchorElement={screenSize.width < 768 ? mobileCommentIconRef.current : commentIconRef.current}
					title="Tell us what you think about this Guide."
					loading={addingComment}
				/>
			)}

			<SignUpModal open={showSignUpModal} onClose={() => setShowSignUpModal(false)} />
		</div>
	);
};

export default GuideDetailsHeadCard;
